import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/PageSEO';
import { RegisterModal } from '../components/RegisterModal';
import { ToolFeedback } from '../components/ToolFeedback';
import { EmailVerificationWall } from '../components/EmailVerificationWall';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Send,
  Loader2,
  MessageSquare,
  Plus,
  Bot,
  User,
  AlertTriangle,
  ChevronDown,
  FileText,
  DollarSign,
  Clock,
  Settings,
  Lock,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialOptions = [
  { id: 'contrato', label: 'Cambio en contrato o condiciones laborales', icon: FileText },
  { id: 'prestaciones', label: 'Cambio en prestaciones', icon: DollarSign },
  { id: 'pago_variable', label: 'Cambio en pago variable', icon: DollarSign },
  { id: 'jornada', label: 'Cambio en jornada laboral', icon: Clock },
  { id: 'otro', label: 'Otro problema laboral', icon: Settings },
];

export default function AdvisorPage() {
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showInitialOptions, setShowInitialOptions] = useState(true);
  const [usageLimitReached, setUsageLimitReached] = useState(false);
  const [usageRemaining, setUsageRemaining] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const pendingMessageRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadSessions();
      checkUsage();
    } else {
      setInitialLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const sessionId = searchParams.get('session');
    if (sessionId && sessions.length > 0) {
      const session = sessions.find((s) => s.session_id === sessionId);
      if (session) {
        setCurrentSession(session);
        loadMessages(sessionId);
      }
    }
  }, [searchParams, sessions]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkUsage = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/usage/status`);
      if (response.ok) {
        const data = await response.json();
        if (data.plan === 'pro') {
          setUsageLimitReached(false);
          setUsageRemaining(null);
        } else {
          const ai = data.features?.ai_advisor;
          if (ai) {
            setUsageRemaining(ai.remaining);
            setUsageLimitReached(ai.remaining <= 0);
          }
        }
      }
    } catch {}
  };

  // Registra en usage_limit_events el momento exacto en que aparece el banner de límite
  // alcanzado. Depende de usageLimitReached (booleano), no se reenvía en cada checkUsage()
  // que vuelve a confirmar que sigue en el límite.
  useEffect(() => {
    if (usageLimitReached) {
      authFetch(`${API_URL}/api/usage-limit/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: 'ai_advisor', event: 'limit_reached' }),
      }).catch(() => { /* non-blocking */ });
    }
  }, [usageLimitReached]);

  const trackUpgradeClick = () => {
    authFetch(`${API_URL}/api/usage-limit/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tool: 'ai_advisor', event: 'upgrade_click' }),
    }).catch(() => { /* non-blocking */ });
  };

  const loadSessions = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/chat/sessions`);
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadMessages = async (sessionId) => {
    try {
      const response = await authFetch(`${API_URL}/api/chat/sessions/${sessionId}/messages`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        setShowInitialOptions(data.length === 0);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/chat/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'Nueva conversación' }),
      });
      if (response.ok) {
        const session = await response.json();
        setSessions((prev) => [session, ...prev]);
        setCurrentSession(session);
        setMessages([]);
        setShowInitialOptions(true);
        return session;
      }
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Error al crear sesión');
    }
    return null;
  };

  const handleOptionSelect = async (option) => {
    if (!isAuthenticated) {
      pendingMessageRef.current = `Estoy enfrentando una situación relacionada con: ${option.label}. ¿Qué información necesitas para ayudarme?`;
      setShowRegisterModal(true);
      return;
    }
    setShowInitialOptions(false);
    let session = currentSession;
    if (!session) {
      session = await createNewSession();
      if (!session) return;
    }
    
    const contextMessage = `Estoy enfrentando una situación relacionada con: ${option.label}. ¿Qué información necesitas para ayudarme?`;
    await sendMessage(contextMessage, session);
  };

  const sendMessage = async (content, session = currentSession) => {
    if (!content.trim()) return;
    if (!session) {
      session = await createNewSession();
      if (!session) return;
    }

    // Add user message to UI immediately
    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content: content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);
    setInput('');
    setLoading(true);
    setShowInitialOptions(false);

    try {
      const response = await authFetch(`${API_URL}/api/chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.session_id,
          content: content,
        }),
      });

      if (response.ok) {
        const aiMessage = await response.json();
        setMessages((prev) => [...prev, aiMessage]);
        checkUsage();
      } else if (response.status === 429) {
        setUsageLimitReached(true);
        setUsageRemaining(0);
        setMessages((prev) => prev.filter((m) => m.message_id !== tempUserMsg.message_id));
      } else {
        toast.error('Error al enviar mensaje');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (!isAuthenticated) {
      pendingMessageRef.current = input;
      setShowRegisterModal(true);
      return;
    }
    sendMessage(input);
  };

  const handleRegisterSuccess = async (registeredUser) => {
    const pending = pendingMessageRef.current;
    pendingMessageRef.current = null;
    // Cuenta recién creada, aún sin verificar: el muro se mostrará en el siguiente
    // render en vez de enviar el mensaje pendiente.
    if (registeredUser?.email_verified === false) return;
    if (pending) {
      // Reload sessions after registration, then send the pending message
      await loadSessions();
      await checkUsage();
      setShowInitialOptions(false);
      const session = await createNewSession();
      if (session) {
        await sendMessage(pending, session);
      }
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <PageSEO
        title="Asesor Laboral con IA | Klarvi"
        description="Resuelve tus dudas laborales gratis con un asesor con IA basado en la Ley Federal del Trabajo. Orientación clara sobre despido, sueldo y más."
        canonical="https://klarvi.org/advisor"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Asesor Laboral con IA - Klarvi",
          "url": "https://klarvi.org/advisor",
          "description": "Orientación laboral gratuita con IA basada en la Ley Federal del Trabajo de México.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "inLanguage": "es-MX",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "MXN" },
        }}
      />
      {isAuthenticated && user?.email_verified === false ? (
        <EmailVerificationWall />
      ) : (
      <>
      <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-200px)] min-h-[500px]" data-testid="advisor-page">
        {/* Sessions Sidebar - Desktop */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <Card className="h-full border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Manrope']">Conversaciones</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={createNewSession}
                  data-testid="new-session-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-2">
              <ScrollArea className="h-[calc(100vh-340px)]">
                <div className="space-y-1">
                  {sessions.map((session) => (
                    <button
                      key={session.session_id}
                      onClick={() => {
                        setCurrentSession(session);
                        loadMessages(session.session_id);
                      }}
                      data-testid={`session-${session.session_id}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors
                        ${currentSession?.session_id === session.session_id
                          ? 'bg-[#2563EB]/10 text-[#2563EB]'
                          : 'hover:bg-muted text-muted-foreground'
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{session.title}</span>
                      </div>
                    </button>
                  ))}
                  {sessions.length === 0 && !initialLoading && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay conversaciones
                    </p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Main Chat Area */}
        <Card className="flex-1 border flex flex-col overflow-hidden">
          <CardHeader className="pb-3 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2563EB] flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg font-['Manrope']">Asesor Laboral IA</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Orientación sobre situaciones laborales en México
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={createNewSession}
                className="lg:hidden"
                data-testid="new-session-mobile-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            </div>
          </CardHeader>

          {/* Messages Area */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full p-4">
              {/* Initial Options */}
              {showInitialOptions && messages.length === 0 && (
                <div className="max-w-2xl mx-auto animate-fade-in">
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-semibold text-[#0F172A] font-['Manrope']">
                      ¿Qué situación laboral estás enfrentando?
                    </h3>
                    <p className="mt-2 text-muted-foreground">
                      Selecciona una opción o escribe tu consulta directamente
                    </p>
                  </div>
                  <div className="grid gap-3">
                    {initialOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleOptionSelect(option)}
                          data-testid={`option-${option.id}`}
                          className="flex items-center gap-3 p-4 rounded-lg border text-left transition-all hover:border-[#2563EB]/50 hover:bg-[#2563EB]/5"
                        >
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <span className="font-medium text-[#0F172A]">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Chat Messages */}
              {messages.map((msg, index) => (
                <div
                  key={msg.message_id}
                  className={`flex gap-3 mb-4 animate-slide-up ${
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-[#0F172A] text-white'
                        : 'bg-muted'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    <span className={`text-xs mt-1 block ${
                      msg.role === 'user' ? 'text-white/60' : 'text-muted-foreground'
                    }`}>
                      {formatTime(msg.created_at)}
                    </span>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}

              {/* Loading Indicator */}
              {loading && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </ScrollArea>
          </div>

          {/* Input Area */}
          <div className="p-4 border-t flex-shrink-0">
            {usageLimitReached ? (
              <div className="text-center py-3 px-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="usage-limit-banner">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    Alcanzaste tus 3 consultas gratuitas de este mes. Actualiza a Pro para continuar sin límites.
                  </span>
                </div>
                <Link to="/precios" onClick={trackUpgradeClick}>
                  <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90" data-testid="view-plans-btn">
                    Ver planes
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="flex gap-2">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Escribe tu consulta..."
                    disabled={loading}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button
                    type="submit"
                    disabled={loading || !input.trim()}
                    className="bg-[#2563EB] hover:bg-[#2563EB]/90"
                    data-testid="send-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
                {usageRemaining !== null && usageRemaining > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {usageRemaining} {usageRemaining === 1 ? 'consulta restante' : 'consultas restantes'} este mes
                  </p>
                )}
              </>
            )}
            {/* Disclaimer - Collapsible */}
            <button
              onClick={() => setShowDisclaimer(prev => !prev)}
              className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
              data-testid="disclaimer-toggle"
            >
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span className="font-medium">Aviso legal</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showDisclaimer ? 'rotate-180' : ''}`} />
            </button>
            {showDisclaimer && (
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed pl-[18px]">
                Las orientaciones de Klarvi se basan en la Ley Federal del Trabajo (LFT) y regulaciones laborales vigentes en México. Esta información es de carácter educativo e informativo únicamente y no constituye asesoría legal, representación legal ni consejo financiero.
              </p>
            )}
          </div>
        </Card>
        <ToolFeedback tool="asesor_ia" />
      </div>
      <RegisterModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        onSuccess={handleRegisterSuccess}
      />
      </>
      )}
    </Layout>
  );
}
